import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import PasskeyCredentialList from '@devnw/cyber-iam/auth/PasskeyCredentialList/PasskeyCredentialList'
import type { PasskeyCredential } from '@devnw/cyber-iam/auth/PasskeyCredentialList/PasskeyCredentialList'
// eslint-disable-next-line import/no-unresolved
import PasskeyRegistrationCard from '@devnw/cyber-iam/auth/PasskeyRegistrationCard/PasskeyRegistrationCard'
import type { RegisterBeginOptions, RegisterCompletePayload } from '@devnw/cyber-iam/auth/protocols/webauthn'
import { HTTPError } from '../../../gen/client/react'
import type { WebAuthnCredentialSummary } from '../../../gen/client/react'
import { useAdminGenClient } from '../api/genClient'
import { useAuth } from '../auth/AuthContext'

/**
 * The registration ceremony session is one-shot server-side
 * (gen/pkg/securex/webauthnx/sessionstore.go's Take()): once consumed,
 * expired, or missing, every one of these three sentinels surfaces as a
 * plain-English 400 with no path forward except starting over. Whatever
 * caused it (a duplicate submit, a second tab, simply taking too long),
 * the remedy is identical, so match all three rather than only the exact
 * string a user happened to report.
 */
const SESSION_ERROR_PATTERN =
  /^webauthnx: session (not found|expired|already consumed)$/

function registrationErrorMessage(e: unknown): string {
  if (e instanceof HTTPError && SESSION_ERROR_PATTERN.test(e.message)) {
    return 'Your passkey registration session expired or was already used. Click "Register a passkey" to start over.'
  }
  return e instanceof Error ? e.message : 'Registration failed'
}

/**
 * WebAuthnCredentialSummary (generated client) -> PasskeyCredential
 * (@devnw/cyber-iam/auth PasskeyCredentialList prop).
 *
 * OIDCPAM Gate-6 Phase 2a — wire mapping: gen/client/react/client.ts's
 * decodeJSON() already snake_case -> camelCase converts every JSON response
 * (fromWire()/convertKeys()), so WebAuthnCredentialSummary is ALREADY
 * camelCase at this boundary. credentialId/alias/transports pass through
 * unchanged; createdAt is an ISO 8601 timestamp on the wire and must be
 * pre-formatted here — PasskeyCredentialList documents its createdAt prop as
 * host-formatted display text ("the component never parses dates").
 */
function toPasskeyCredential(cred: WebAuthnCredentialSummary): PasskeyCredential {
  return {
    credentialId: cred.credentialId,
    alias: cred.alias,
    createdAt: formatCreatedAt(cred.createdAt),
    transports: cred.transports,
  }
}

function formatCreatedAt(iso: string | undefined): string | undefined {
  if (!iso) return undefined
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return undefined
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('md')};
`

const PageHeading = styled.h1`
  margin: 0;
  font-size: ${({ theme }) => asUnified(theme).font.sizes.lg};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text.primary};
`

const RegistrationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('sm')};
`

const AliasFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('xxs')};
`

const AliasFieldLabel = styled.label`
  font-size: ${({ theme }) => asUnified(theme).font.sizes.sm};
  font-weight: 600;
  color: ${({ theme }) => asUnified(theme).text.primary};
`

const AliasFieldInput = styled.input`
  min-height: 44px;
  max-width: 320px;
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xs')} ${ut(asUnified(theme)).space('sm')}`};
  border: 1px solid ${({ theme }) => ut(asUnified(theme)).borderColor()};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius()}px;
  font-size: ${({ theme }) => asUnified(theme).font.sizes.sm};
  color: ${({ theme }) => asUnified(theme).text.primary};
  background: ${({ theme }) => asUnified(theme).bg.canvas};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => asUnified(theme).colors?.primary};
    outline-offset: 1px;
  }
`

/**
 * Non-blocking warning surface for the "registration succeeded but naming it
 * failed" case (see handleComplete below) — PasskeyRegistrationCard has no
 * slot for this, so the page renders it itself, mirroring the pre-migration
 * MUI Alert's severity="warning" role="alert".
 */
const WarningAlert = styled.div`
  align-self: stretch;
  border: 1px solid ${({ theme }) => asUnified(theme).state?.warning ?? '#92400e'};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius()}px;
  padding: ${({ theme }) => ut(asUnified(theme)).space('sm')};
  font-size: ${({ theme }) => asUnified(theme).font.sizes.sm};
  color: ${({ theme }) => asUnified(theme).state?.warning ?? '#92400e'};
`

interface WebAuthnCredentialsProps {
  /** Called once after a passkey is successfully registered. Optional —
   *  existing callers (the standalone /admin/security-keys page) don't need
   *  it; PamPasskeyGate uses it to stop showing the enrollment prompt. */
  onRegistered?: () => void
}

/**
 * Self-service WebAuthn passkey registration. Without a registered
 * credential, the AAL3 step-up ceremony required by privileged actions
 * (e.g. PAM grant activation) has nothing to assert against and can never
 * succeed — this page is the only way to close that gap.
 *
 * OIDCPAM Gate-6 Phase 2a: migrated off MUI onto
 * @devnw/cyber-iam/auth/PasskeyRegistrationCard + PasskeyCredentialList.
 * PasskeyRegistrationCard owns the navigator.credentials.create(...)
 * ceremony internally (via the shared runRegistrationCeremony helper in
 * auth/protocols/webauthn — the same base64url<->ArrayBuffer codec and
 * ceremony orchestration this page used to inline) and calls back into this
 * page's begin()/complete() for the two server round-trips
 * (postRegisterBegin / postRegisterComplete). This page still owns every API
 * call, the alias-on-register naming, and the credential list's
 * rename/delete mutations — nothing about the wire protocol changed.
 */
export const WebAuthnCredentials = ({ onRegistered }: WebAuthnCredentialsProps = {}): JSX.Element => {
  const client = useAdminGenClient()
  const { claims } = useAuth()
  const [credentials, setCredentials] = useState<WebAuthnCredentialSummary[]>([])
  const [listError, setListError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [savingAliasId, setSavingAliasId] = useState<string | null>(null)
  const [newPasskeyAlias, setNewPasskeyAlias] = useState('')
  const [aliasWarning, setAliasWarning] = useState<string | null>(null)
  // Hand-off between begin() and complete(): PasskeyRegistrationCard always
  // fully resolves begin() before invoking the ceremony and then complete(),
  // and its own reentrancy guard (inFlight ref) rules out a second overlapping
  // run, so a plain ref is a safe (and simplest) way to carry the session ID
  // the server minted in begin() through to complete()'s request body.
  const sessionIdRef = useRef<string>('')

  const refreshCredentials = useCallback(async (): Promise<void> => {
    try {
      const resp = await client.webAuthnListCredentials({})
      setCredentials(resp.credentials)
      setListError(null)
    } catch (e: unknown) {
      setListError(e instanceof Error ? e.message : 'Failed to load passkeys')
    }
  }, [client])

  useEffect(() => {
    refreshCredentials()
  }, [refreshCredentials])

  const handleDelete = useCallback(async (credentialId: string): Promise<void> => {
    setDeletingId(credentialId)
    try {
      await client.webAuthnDeleteCredential({ credentialId })
      setCredentials((prev) => prev.filter((c) => c.credentialId !== credentialId))
      setListError(null)
    } catch (e: unknown) {
      setListError(e instanceof Error ? e.message : 'Failed to remove passkey')
    } finally {
      setDeletingId(null)
    }
  }, [client])

  const handleRename = useCallback(async (credentialId: string, alias: string): Promise<void> => {
    setSavingAliasId(credentialId)
    try {
      await client.webAuthnSetCredentialAlias({ credentialId, alias })
      setCredentials((prev) =>
        prev.map((c) => (c.credentialId === credentialId ? { ...c, alias } : c))
      )
      setListError(null)
    } catch (e: unknown) {
      setListError(e instanceof Error ? e.message : 'Failed to save the passkey name')
    } finally {
      setSavingAliasId(null)
    }
  }, [client])

  const handleBegin = useCallback(async (): Promise<RegisterBeginOptions> => {
    setAliasWarning(null)
    if (!claims?.sub) {
      throw new Error(
        'Unable to register a passkey: no signed-in user was found. Please sign in again and retry.'
      )
    }
    try {
      const begin = await client.postRegisterBegin({ userId: claims.sub })
      sessionIdRef.current = begin.sessionId
      return begin.options as unknown as RegisterBeginOptions
    } catch (e: unknown) {
      throw new Error(registrationErrorMessage(e))
    }
  }, [client, claims])

  const handleComplete = useCallback(async (payload: RegisterCompletePayload): Promise<void> => {
    try {
      const completed = await client.postRegisterComplete({
        sessionId: sessionIdRef.current,
        credential: payload,
      })

      // Naming the passkey up front means the user doesn't have to come
      // back later to tell "MacBook Touch ID" apart from "YubiKey 5C" in
      // the list below — a failure here must NOT be treated as the
      // registration itself failing (the credential already exists and is
      // usable for step-up); it just leaves this one passkey unnamed,
      // exactly like registering with the name field left blank.
      const trimmedAlias = newPasskeyAlias.trim()
      if (trimmedAlias) {
        try {
          await client.webAuthnSetCredentialAlias({
            credentialId: completed.credentialId,
            alias: trimmedAlias,
          })
        } catch (aliasErr: unknown) {
          setAliasWarning(
            'The name could not be saved: ' +
              (aliasErr instanceof Error ? aliasErr.message : 'unknown error') +
              '. You can rename it below.'
          )
        }
      }
      setNewPasskeyAlias('')
    } catch (e: unknown) {
      throw new Error(registrationErrorMessage(e))
    }
  }, [client, newPasskeyAlias])

  const handleRegistered = useCallback((): void => {
    // refreshCredentials never rejects (it records fetch failures in
    // listError internally), so its returned promise is safe to leave
    // unawaited here — handleRegistered is an onX prop and can't be async.
    refreshCredentials()
    onRegistered?.()
  }, [refreshCredentials, onRegistered])

  return (
    <PageContainer>
      {/* Task 11 (fix-wave 2026-07-24): this list is scoped to the CALLER's
          OWN passkeys (server-verified — see webauthn_credentials_crud.go's
          owner resolution from the authenticated context, not any
          caller-supplied id). The heading must say so explicitly: a bare
          "Passkeys" heading, filed under AdminLayout's tenant-wide "Access"
          nav group, previously read as an app-wide credential inventory —
          that misleading IA (not a scoping bug) is what the user reported.
          This same heading also renders inside PamPasskeyGate.tsx's
          enrollment-prompt card (~line 89), where it remains accurate. */}
      <PageHeading>Your passkeys</PageHeading>
      <RegistrationSection>
        <AliasFieldWrapper>
          <AliasFieldLabel htmlFor="new-passkey-alias">
            Name this passkey (optional)
          </AliasFieldLabel>
          <AliasFieldInput
            id="new-passkey-alias"
            type="text"
            placeholder="e.g. MacBook Touch ID"
            value={newPasskeyAlias}
            onChange={(e) => setNewPasskeyAlias(e.target.value)}
            maxLength={128}
          />
        </AliasFieldWrapper>
        {aliasWarning != null && <WarningAlert role="alert">{aliasWarning}</WarningAlert>}
        <PasskeyRegistrationCard
          testID="webauthn-registration"
          title="Passkey / security key"
          description="Register a passkey or security key to satisfy the step-up authentication required by privileged actions (e.g. activating a PAM grant)."
          begin={handleBegin}
          complete={handleComplete}
          onRegistered={handleRegistered}
        />
      </RegistrationSection>

      <PasskeyCredentialList
        testID="webauthn-credentials"
        credentials={credentials.map(toPasskeyCredential)}
        onRename={handleRename}
        onDelete={handleDelete}
        busyCredentialId={deletingId ?? savingAliasId ?? undefined}
        error={listError ?? undefined}
      />
    </PageContainer>
  )
}
